import { cn } from "../../lib/cn";

/**
 * Phase 2 primitive — `<ProgressBar>` replaces `.minibar`, `.phase-pbar`,
 * `.wf-track`, `.sbar-track`, `.urg-track`, `.aud-card-pbar`, `.kb-card-progress`
 * etc. A flat track with a coloured fill; the caller chooses colour + width.
 *
 * Props:
 *   pct       — 0..100. Clamped.
 *   tone      — default|success|warn|crit|info|plan (drives fill colour)
 *   color     — optional override for the fill (any CSS colour string)
 *   size      — xs|sm|md (track height: 3px / 4px / 6px)
 *   width     — optional fixed track width; otherwise fills container
 *   showLabel — append `NN%` after the bar (mono font)
 */
const FILL_TONE = {
  default: "bg-rc-blue-500",
  info:    "bg-rc-accent-strong",
  success: "bg-[#10c981]",
  warn:    "bg-[#f59e0b]",
  crit:    "bg-rc-red",
  plan:    "bg-[#a78bfa]",
};

const SIZE = {
  xs: "h-[3px]",
  sm: "h-1",
  md: "h-1.5",
};

export default function ProgressBar({
  pct = 0,
  tone = "default",
  color,
  size = "sm",
  width,
  showLabel,
  className,
}) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className={cn("inline-flex items-center gap-2", className)}>
      <div
        className={cn(
          "rounded-[3px] bg-rc-border overflow-hidden inline-block",
          SIZE[size] || SIZE.sm,
        )}
        style={{ width: width ?? "100%" }}
      >
        <div
          className={cn(
            "h-full rounded-[3px] transition-[width] duration-300",
            !color && (FILL_TONE[tone] || FILL_TONE.default),
          )}
          style={{
            width: `${clamped}%`,
            ...(color ? { background: color } : {}),
          }}
        />
      </div>
      {showLabel && (
        <span className="font-mono text-[10.5px] text-rc-text-dim min-w-[28px] text-right">
          {Math.round(clamped)}%
        </span>
      )}
    </div>
  );
}
